'use client'

import type { ProductPriceProps } from '@fukku/price/types'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useProductPrices } from 'prices/hook/useProductPrices/useProductPrices'
import { getCrossedOutPrices } from 'prices/utils/getCrossedOutPrices/getCrossedOutPrices'
import { useEffect } from 'react'
import { isBot } from 'utils/isBot/IsBot'
import { usePricesStore } from '../../context/PricesProvider'
import { Price } from '../price/Price'

/**
 * @description
 * Renders the price and discounts of a product on the client side with price revalidation.
 * This component must be used within a PricesProvider
 *
 * @example
 * <ProductPrice product="123" color="01" />
 *
 * @see {@link ServerProductPrice}
 * @see {@link Price}
 * @link https://schema.org/docs/gs.html#microdata_itemscope_itemtype_itemprop microdata
 */
export const ProductPrice = ({
	product,
	color,
	prices: pricesFromServer,
	shouldRevalidate = true,
	hidePriceHistory = false,
	dataTestId,
}: ProductPriceProps) => {
	const { updatePrices } = usePricesStore()
	const masterData = useMasterData()
	const {
		country: { hasPriceHistory },
	} = masterData

	const shouldFetch = shouldRevalidate && !isBot()

	const { data, isValidating } = useProductPrices(
		masterData,
		product,
		shouldFetch
	)

	useEffect(() => {
		if (!isValidating && data && shouldFetch) {
			const newProductPrices = data?.[color] ?? pricesFromServer

			if (!newProductPrices?.price) {
				return
			}

			const { crossedOutPrices, allCrossedOutWithoutLowest } =
				getCrossedOutPrices({
					prices: newProductPrices,
					hidePriceHistory,
					hasPriceHistory,
				})

			updatePrices({
				price: newProductPrices.price,
				discountRate: newProductPrices.discountRate,
				crossedOutPrices,
				allCrossedOutWithoutLowest,
			})
		}
	}, [data, isValidating])

	const productPrices = data?.[color] ?? pricesFromServer

	if (!productPrices?.price) {
		return null
	}

	return <Price dataTestId={dataTestId} />
}
